"use client";

import React, { useEffect } from "react";
import Pagination from "./Pagination";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "@/src/navigation";

interface PaginatedContentProps {
  children: React.ReactNode;
  itemsPerPage?: number;
  className?: string;
  wrapperClassName?: string;
}

const PaginatedContent: React.FC<PaginatedContentProps> = ({
  children,
  itemsPerPage = 9,
  className = "",
  wrapperClassName = "",
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const items = React.Children.toArray(children);
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const urlPageValue = searchParams.get("page");

  function getValidPage(str: string | null) {
    const number = Number(str);
    if (!number || isNaN(number) || number < 1) return 1;
    if (number > totalPages) return totalPages;
    return number;
  }
  const currentPage = getValidPage(urlPageValue);

  useEffect(() => {
    if (currentPage !== Number(urlPageValue)) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", currentPage.toString());
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, [currentPage, urlPageValue, pathname, router, searchParams]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = items.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", page.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  if (items.length === 0) return null;

  return (
    <div className={`flex flex-col gap-10 ${wrapperClassName}`}>
      <div className={className}>{currentItems}</div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default PaginatedContent;
